import React, { useMemo, useState } from "react";
import {
  makeStyles,
  Label,
  Dropdown,
  Option,
  OptionGroup,
  Spinner,
  useId,
} from "@fluentui/react-components";
import { FilterDismiss16Regular } from "@fluentui/react-icons";
import { getCountryName } from "../lib/countries";

const useStyles = makeStyles({
  dropdown: {
    minWidth: 0,
    width: "100%",
  },
});

interface CountryDropdownProps {
  countries: string[];
  isLoading: boolean;
  selectedCountries: string[];
  onSelectionChange: (countries: string[]) => void;
}

export const CountryDropdown: React.FC<CountryDropdownProps> = ({
  countries,
  isLoading,
  selectedCountries,
  onSelectionChange,
}) => {
  const styles = useStyles();
  const labelId = useId("country-label");
  const [isOpen, setIsOpen] = useState(false);
  let allCountriesValue = "__all_countries__";
  while (
    countries.includes(allCountriesValue) ||
    selectedCountries.includes(allCountriesValue)
  ) {
    allCountriesValue += "_";
  }

  const sortedCountries = useMemo(
    () =>
      [...countries]
        .map((code) => ({ code, name: getCountryName(code) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [countries],
  );

  return (
    <>
      <Label id={labelId} weight="semibold">
        Country
      </Label>
      {isLoading ? (
        <Spinner size="tiny" />
      ) : (
        <Dropdown
          aria-labelledby={labelId}
          className={styles.dropdown}
          multiselect
          open={isOpen}
          onOpenChange={(_, data) => setIsOpen(data.open)}
          placeholder="All countries"
          value={
            selectedCountries.length > 0
              ? `${selectedCountries.length} selected`
              : ""
          }
          selectedOptions={selectedCountries}
          onOptionSelect={(_, data) => {
            if (data.optionValue === allCountriesValue) {
              onSelectionChange([]);
              setIsOpen(false);
            } else {
              onSelectionChange(data.selectedOptions);
            }
          }}
          size="small"
        >
          <OptionGroup>
            <Option
              value={allCountriesValue}
              text="Reset filter"
              disabled={selectedCountries.length === 0}
              role="menuitem"
              aria-checked={undefined}
              checkIcon={null}
            >
              <FilterDismiss16Regular aria-hidden="true" />
              Reset filter
            </Option>
          </OptionGroup>
          <OptionGroup>
            {sortedCountries.map(({ code, name }) => (
              <Option key={code} value={code}>
                {name}
              </Option>
            ))}
          </OptionGroup>
        </Dropdown>
      )}
    </>
  );
};
